<script lang="ts">
    import { createEventDispatcher } from 'svelte';

    import { api } from '../js/api.js';
    import { auth } from '../js/auth.js';
    import { toast } from "../js/toast.js";
    import type { ApiResponse } from '../js/apiTypes.js';
    import { getNestedProperty } from '../js/utils.js';
    import {
        emptyTableData,
        getSelectOptions,
        optionProp,
        type FormDataRecord,
        type MediaFile,
        type TableData,
        type TableField,
    } from '../js/tableTypes.js';

    export let modelId: string | null = '';
    export let modelName = '';
    export let apiResponse = false;
    export let displayModal = false;
    export let tableData: TableData = emptyTableData;
    export let customSubmit: ((data: FormData) => void) | null = null;

    const dispatch = createEventDispatcher<{
        saved: { detail: unknown };
        mediaDeleted: void;
        fileSelected: { url: string };
        changeEmail: void;
    }>();

    let currentId: string | null = '';
    let isEditing = false;
    let displayConfirm = false;
    let dirty = false;
    let selectedFiles: FileList | null = null;
    let fileInput: HTMLInputElement;
    let singleMediaUrl = './src/assets/avatar.png';
    let errors: Record<string, string> = {};

    function triggerFileInput() {
        fileInput.click();
    }

    async function handleImageDelete(event: CustomEvent<{ uuid: string }>) {
        const deletedUuid = event.detail.uuid;
        await api.crudDelete('media-file', deletedUuid);
        toast.showNotification(!!$api.success, $api.message ?? '');
        const mediaFiles = formData.media_files ?? [];
        formData.media_files = mediaFiles.filter((image: MediaFile) => image.uuid !== deletedUuid);
        if (Object.prototype.hasOwnProperty.call(formData, 'media_file')) {
            delete formData.media_file;
            singleMediaUrl = './src/assets/avatar.png';
        }
        dispatch('mediaDeleted');
    }

    const handleFileSelection = () => {
        if (!selectedFiles || selectedFiles.length === 0) return;
        setDirty();
        if (tableData.details.hasMediaFiles) {
            const existing = formData.media_files ?? [];
            formData.media_files = [
                ...existing,
                ...Array.from(selectedFiles).map((file) => ({
                    url: URL.createObjectURL(file),
                    file_name: file.name,
                })),
            ];
        } else {
            const file = selectedFiles[0];
            const url = URL.createObjectURL(file);
            if (!formData.media_file) {
                formData.media_file = {};
            }
            formData.media_file.url = url;
            singleMediaUrl = url;
            dispatch('fileSelected', { url });
        }
    };

    const confirmHide = () => {
        if (dirty) {
            displayConfirm = true;
            return;
        }
        hide();
    };

    const hide = () => {
        displayModal = false;
        isEditing = false;
    };

    const setDirty = () => {
        dirty = true;
    };

    let formData: FormDataRecord = {};
    let isViewMode = true;

    const toggleEditMode = () => {
        isViewMode = !isViewMode;
    };

    async function save() {
        if (validateForm()) {
            const data = new FormData();

            if (Object.prototype.hasOwnProperty.call(formData, 'media_file')) {
                delete formData.media_file;
            }

            if (Object.prototype.hasOwnProperty.call(formData, 'media_files')) {
                delete formData.media_files;
            }

            if (selectedFiles) {
                if (tableData.details.hasMediaFiles) {
                    Array.from(selectedFiles).forEach((file, index) => {
                        data.append(`media_files[${index}]`, file);
                    });
                } else {
                    data.append('media_file', selectedFiles[0]);
                }
            }

            tableData.fields.forEach((field: TableField) => {
                if (field.editable || modelId === null) {
                    const key = field.model_key !== undefined ? field.model_key : field.key;
                    const value = formData[field.key];
                    data.append(key, value != null ? String(value) : '');
                }
            });

            if (customSubmit) {
                customSubmit(data);
            } else {
                let response: ApiResponse;
                if (modelId === null) {
                    response = await api.crudCreate(modelName, data) as ApiResponse;
                } else {
                    response = await api.crudUpdate(modelName, modelId, data) as ApiResponse;
                }

                if (response.success) {
                    hide();
                    dispatch('saved', { detail: response.data });
                    dirty = false;
                    apiResponse = true;
                    isViewMode = true;
                    if (modelName === 'user' && modelId === $auth.user?.uuid) {
                        await auth.userRefresh();
                    }
                }
                toast.showNotification(!!$api.success, $api.message ?? '');
            }
        }
    }

    function validateForm() {
        let valid = true;
        errors = {};
        tableData.fields.forEach((element: TableField) => {
            if (element.isMandatory && !formData[element.key]) {
                errors[element.key] = element.mandatoryMessage || `${element.label} is required`;
                valid = false;
            }
        });
        return valid;
    }

    const edit = () => {
        isEditing = true;
    };

    $: if (displayModal === true) {
        refreshData();
    }

    $: {
        if (modelId !== currentId || modelId === null) {
            refreshData();
        }
        if (currentId === null) {
            edit();
        }
    }

    async function refreshData() {
        formData = {};
        errors = {};

        if (modelId !== null) {
            await fetchData();
        }

        updateFormData();
        currentId = modelId;
        dirty = false;
    }

    async function fetchData() {
        if (modelId === null) return;
        const response = await api.crudGet(modelName, modelId) as ApiResponse<FormDataRecord>;
        formData = { ...(response.data ?? {}) };
        singleMediaUrl = formData.media_file?.url ?? './src/assets/avatar.png';
    }

    function updateFormData() {
        for (const field of tableData.fields) {
            if (field.type === 'select' && field.value) {
                formData[field.key] = getNestedProperty(formData, field.value);
            }
        }
        selectedFiles = null;
    }

    interface CountryOption {
        code: string;
        name: string;
    }

    function getCountryName(countryCode: string | undefined): string {
        if (!countryCode || !$api.lists) {
            return '';
        }
        const countries = getSelectOptions($api.lists, 'countries') as unknown as CountryOption[];
        const country = countries.find((c) => c.code === countryCode);
        return country ? country.name : '';
    }

    function fieldStringValue(key: string): string {
        const value = formData[key];
        return value == null ? '' : String(value);
    }

    function fieldBoolValue(key: string): boolean {
        return Boolean(formData[key]);
    }
</script>

<style>
    .hidden {
        display: none;
    }

    .error {
        color: rgb(228, 27, 27);
        font-size: 0.875em;
    }

    .form-actions {
        justify-content: flex-end;
    }

    .country-display {
        padding: 0.75rem 1rem;
        font-size: inherit;
        color: var(--grey);
        background-color: var(--greyish);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-button);
        min-height: 2.5rem;
        display: flex;
        align-items: center;
    }
</style>

{#if $auth.token}
        <h2 class="ui header">
            {tableData.details.titleEdit ? tableData.details.titleEdit : modelName.charAt(0).toUpperCase() + modelName.slice(1)}
        </h2>
        <div class="ui profile">
                <h2 class="ui header">

                    {#if tableData.details.hasMediaFile}
                        <div class="field">
                            <input
                                type="file"
                                bind:this={fileInput}
                                class="hidden"
                                bind:files={selectedFiles}
                                on:change={handleFileSelection}
                                accept={tableData.details.mediaFileType}
                            />
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                            <img
                                on:click={() => triggerFileInput()}
                                src={singleMediaUrl}
                                alt="Media File"
                                class="ui image"
                                style="width: 130px; height: 130px;"
                                />
                                <br/>
                        </div>
                    {/if}
                </h2>

                <form class="ui form grid">
                    {#each tableData.fields as field (field.key)}
                        <div class="field" class:multiple-columns={field.multipleColumns}>
                            <label for="{field.key}">
                                {field.label}
                            </label>
                            {#if field.type === 'select'}
                                {#if field.key === 'country' && isViewMode}
                                    <div class="country-display">
                                        {getCountryName(fieldStringValue(field.key)) || 'Not set'}
                                    </div>
                                {:else}
                                    <select bind:value={formData[field.key]} disabled={isViewMode || !field.editable} on:input={setDirty}>
                                        <option value="">Select {field.label}</option>
                                        {#if field.select}
                                            {#each getSelectOptions($api.lists, field.select.data_list) as option (optionProp(option, field.select.key))}
                                                <option value={optionProp(option, field.select.key)}>
                                                    {optionProp(option, field.select.display)}
                                                </option>
                                            {/each}
                                        {/if}
                                    </select>
                                {/if}
                            {:else if field.type === 'textarea'}
                                <textarea id="{field.key}" bind:value={formData[field.key]} disabled={!field.editable} on:input={setDirty}></textarea>
                            {:else if field.type === 'checkbox'}
                                <input id="{field.key}" type="checkbox" checked={fieldBoolValue(field.key)} disabled={!field.editable} />
                            {:else if field.type === 'date'}
                                <input id="{field.key}" type="date" bind:value={formData[field.key]} disabled={!field.editable} on:input={setDirty} />
                            {:else if field.type === 'time'}
                                <input id="{field.key}" type="time" bind:value={formData[field.key]} disabled={!field.editable} on:input={setDirty} />
                            {:else if field.key === 'email'}
                                <input id="{field.key}" type="email" bind:value={formData[field.key]} disabled={true} on:input={setDirty} />
                            {:else}
                                <input id="{field.key}" type="text" bind:value={formData[field.key]} disabled={isViewMode || !field.editable} on:input={setDirty} />
                            {/if}
                            {#if errors[field.key]}
                                <span class="error">{errors[field.key]}</span><br>
                            {/if}
                        </div>
                    {/each}
                    <div class="form-actions" style="display: flex; gap: 12px; align-items: center;">
                        {#if isViewMode}
                            <button type="button" on:click={toggleEditMode} class="ui button primary blue">Edit Profile</button>
                            <button type="button" on:click={() => dispatch('changeEmail')} class="ui button secondary">Change Email</button>
                        {:else}
                            <button type="button" on:click={save} class="ui button primary blue">Save Changes</button>
                            <button type="button" on:click={() => { toggleEditMode(); refreshData(); }} class="ui button">Cancel</button>
                        {/if}
                    </div>
                </form>
            </div>
{/if}
